import { CopyIcon } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useChat } from '@/components/providers/ChatProvider';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

import type { CatalogMap, RequirementsMap } from '../-data';
import { Example } from '../../integrations/-components/Example';

interface Props {
  catalog: CatalogMap;
  requirements: RequirementsMap;
}

export const Catalog = ({ catalog, requirements }: Props): JSX.Element => {
  const { setPrompt } = useChat();
  const { formatMessage } = useIntl();
  const { toast } = useToast();

  return (
    <Accordion
      type="single"
      collapsible
      className="w-full"
      defaultValue={Object.keys(catalog)[0]}
    >
      <div className="flex flex-col gap-2">
        {Object.values(catalog).map(({ id, title, questions }) => (
          <AccordionItem
            value={id}
            key={id}
            className="border rounded-md py-0 px-2"
          >
            <AccordionTrigger>
              <FormattedMessage id={title} />
            </AccordionTrigger>
            <AccordionContent className="flex flex-col gap-2 pb-2">
              {Object.values(questions).map(
                ({ id: questionId, requirementIds, title: questionTitle }) => {
                  const requirementsList = requirementIds.map(
                    requirementId => ({
                      ...requirements[requirementId]!,
                    }),
                  );

                  return (
                    <div
                      className="flex flex-row items-center gap-2 w-full"
                      key={questionId}
                    >
                      <Example
                        title={questionTitle}
                        requirements={requirementsList}
                        onClick={() => {
                          setPrompt(
                            formatMessage({
                              id: questionTitle,
                            }),
                          );
                        }}
                      />
                      <Button
                        variant="ghost"
                        onClick={() => {
                          navigator.clipboard.writeText(
                            formatMessage({
                              id: questionTitle,
                            }),
                          );
                          toast({ title: 'global.copiedToClipboard' });
                        }}
                        type="button"
                      >
                        <CopyIcon className="cursor-pointer h-4 w-4" />
                      </Button>
                    </div>
                  );
                },
              )}
            </AccordionContent>
          </AccordionItem>
        ))}
      </div>
    </Accordion>
  );
};
